import { DATABASE_ROW_SHARD_ORDER_ARRAY_KEY } from '@blocksuite/affine-model';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import { type DataViewDataType, type ViewMeta } from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  DatabaseShardDirectoryPartsMissingError,
  databaseShardDirectoryShardRowStart,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';

import { type DataSourceWriteActor } from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import { requireShardDirectoryRowCount } from './data-source-shard-directory.js';
import { moveShardedRowInLocalDocs } from './data-source-shard-writing.js';
import {
  addView,
  deleteView,
  moveViewTo,
  updateView,
} from './utils/block-utils.js';
import { databaseBlockViewMap } from './views/index.js';

export function rowAdd(
  this: DatabaseSourceRuntime,
  insertPosition: InsertToPosition | number
): string {
  const rowId = this._model.store.workspace.idGenerator();
  return this.rowAddWithId(rowId, insertPosition).rowId;
}

export function getShardedInsertIndex(
  this: DatabaseSourceRuntime,
  insertPosition: InsertToPosition | number
): number {
  const total = requireShardDirectoryRowCount(this);
  if (typeof insertPosition === 'number')
    return Math.max(0, Math.min(insertPosition, total));
  if (insertPosition === 'start') return 0;
  if (insertPosition === 'end') return total;
  // The anchor position counts from the row its directory part starts at.
  // An anchor in a part not loaded yet waits for the parts, which it
  // requests, instead of falling back to the end.
  const directory = this.getLoadedShardDirectory(0);
  const partsMissing = () =>
    new DatabaseShardDirectoryPartsMissingError(
      this.databaseId,
      directory.missingParts.map(part => part.partDocId)
    );
  if (!this.rowExists(insertPosition.id)) {
    if (directory.missingParts.length) {
      this.getCompleteShardDirectory();
      throw partsMissing();
    }
    return total;
  }
  const addressed = this.resolveLocalRows([insertPosition.id]).get(
    insertPosition.id
  );
  const shardRowStart =
    addressed &&
    databaseShardDirectoryShardRowStart(
      directory,
      shard => shard.shardId === addressed.shard.shardId
    );
  if (
    addressed &&
    shardRowStart === undefined &&
    directory.missingParts.length
  ) {
    this.getCompleteShardDirectory();
    throw partsMissing();
  }
  if (!addressed || shardRowStart === undefined)
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database insertion anchor could not be verified.'
    );
  const localIndex = addressed.doc.spaceDoc
    .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
    .toArray()
    .map(String)
    .indexOf(insertPosition.id);
  if (localIndex < 0)
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database insertion anchor is absent from its shard.'
    );
  const targetIndex = shardRowStart + localIndex;
  return insertPosition.before ? targetIndex : targetIndex + 1;
}

export function rowDelete(
  this: DatabaseSourceRuntime,
  ids: string[],
  actor: DataSourceWriteActor = 'user'
): void {
  this.assertFileIndexRowMutationAllowed(actor, 'Row deletion');
  this.rowDeleteSharded(ids);
}

export function rowDeleteSharded(
  this: DatabaseSourceRuntime,
  ids: string[]
): void {
  this.deleteShardedRowsFromLocalDocs(ids);
}

export function rowMove(
  this: DatabaseSourceRuntime,
  rowId: string,
  position: InsertToPosition
): void {
  this.assertFileIndexRowMutationAllowed('user', 'Row move');
  moveShardedRowInLocalDocs.call(
    this,
    rowId,
    this.getShardedInsertIndex(position)
  );
}

type ViewChange =
  | { kind: 'add'; view: DataViewDataType }
  | { kind: 'delete'; id: string };
const pendingViewChanges = new WeakMap<object, Set<ViewChange>>();
/** Reserved IDs may be selected in the same queued write sequence as creation. */
export function isPendingDatabaseView(store: object, id: string): boolean {
  let present = false;
  for (const change of pendingViewChanges.get(store) ?? []) {
    if (change.kind === 'add' && change.view.id === id) present = true;
    if (change.kind === 'delete' && change.id === id) present = false;
  }
  return present;
}
function previewViews(context: DatabaseSourceRuntime): DataViewDataType[] {
  let views = Array.from(context._model.props.views);
  for (const change of pendingViewChanges.get(context.storageDoc) ?? []) {
    if (change.kind === 'add') {
      // A synchronous live observer can read between apply and promise settlement.
      if (!views.some(view => view.id === change.view.id))
        views.push(change.view);
    } else if (views.length > 1)
      views = views.filter(view => view.id !== change.id);
  }
  return views;
}
function reserveViewChange(
  context: DatabaseSourceRuntime,
  change: ViewChange
): () => void {
  const views = previewViews(context);
  const next =
    change.kind === 'add'
      ? views.concat(change.view)
      : views.length > 1
        ? views.filter(view => view.id !== change.id)
        : views;
  // Same constant and schema check as the staged fail-closed validation.
  context.assertFileIndexRootSchemaWithinLimits({ views: next });
  let pending = pendingViewChanges.get(context.storageDoc);
  if (!pending)
    pendingViewChanges.set(context.storageDoc, (pending = new Set()));
  pending.add(change);
  return () => {
    pending.delete(change);
    if (!pending.size) pendingViewChanges.delete(context.storageDoc);
  };
}

export function viewDataAdd(
  this: DatabaseSourceRuntime,
  viewData: DataViewDataType
): string {
  const release = reserveViewChange(this, { kind: 'add', view: viewData });
  this.captureStorageSync();
  const mutate = (stage: typeof this._model) => {
    addView(stage, viewData);
    try {
      this.assertFileIndexRootSchemaWithinLimits({}, stage);
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
  };
  this.mutateStorageRoot(mutate, 'schema', true, release);
  return viewData.id;
}

export function viewDataDelete(
  this: DatabaseSourceRuntime,
  viewId: string
): void {
  const views = this.viewDataList$.value;
  const index = views.findIndex(view => view.id === viewId);
  const nextViewId =
    index >= 0
      ? (views[index + 1]?.id ??
        views[index - 1]?.id ??
        views.find(view => view.id !== viewId)?.id)
      : undefined;

  this.captureStorageSync();
  const release = reserveViewChange(this, { kind: 'delete', id: viewId });
  this.mutateStorageRoot(
    stage => deleteView(stage, viewId),
    'schema',
    true,
    release
  );
  if (index >= 0) this.pruneLocalUiStateForDeletedView(viewId, nextViewId);
}

export function viewDataDuplicate(
  this: DatabaseSourceRuntime,
  id: string
): string {
  this.captureStorageSync();
  const newId = this._model.store.workspace.idGenerator();
  const original = previewViews(this).find(view => view.id === id);
  const release = original
    ? reserveViewChange(this, {
        kind: 'add',
        view: { ...original, id: newId },
      })
    : () => {};
  this.mutateStorageRoot(
    stage => {
      const view = stage.props.views.find(view => view.id === id);
      if (view)
        addView(stage, { ...JSON.parse(JSON.stringify(view)), id: newId });
      moveViewTo(stage, newId, { id, before: false });
      try {
        this.assertFileIndexRootSchemaWithinLimits({}, stage);
      } catch {
        throw new PublicationError('LIMIT_EXCEEDED');
      }
    },
    'schema',
    true,
    release
  );
  return newId;
}

export function viewDataGet(
  this: DatabaseSourceRuntime,
  viewId: string
): DataViewDataType | undefined {
  return this.viewDataList$.value.find(data => data.id === viewId)!;
}

export function viewDataMoveTo(
  this: DatabaseSourceRuntime,
  id: string,
  position: InsertToPosition
): void {
  this.captureStorageSync();
  this.mutateStorageRoot(stage => moveViewTo(stage, id, position));
}

export function viewDataUpdate<ViewData extends DataViewDataType>(
  this: DatabaseSourceRuntime,
  id: string,
  updater: (data: ViewData) => Partial<ViewData>
): void {
  this.captureStorageSync();
  const mutate = (stage: typeof this._model) => {
    updateView(stage, id, updater);
    try {
      this.assertFileIndexRootSchemaWithinLimits({}, stage);
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
  };
  this.mutateStorageRoot(mutate);
}

export function viewMetaGet(
  this: DatabaseSourceRuntime,
  type: string
): ViewMeta {
  const view = databaseBlockViewMap[type];
  if (!view) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Unknown view type: ${type}`
    );
  }
  return view;
}

export function viewMetaGetById(
  this: DatabaseSourceRuntime,
  viewId: string
): ViewMeta | undefined {
  const view = this.viewDataGet(viewId);
  if (!view) {
    return;
  }
  return this.viewMetaGet(view.mode);
}
